import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { vehicleDocumentSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { VehicleDocument } from './model'
import { Vehicle } from '../vehicles/model'
import * as vehiclesActionCreators from '../vehicles/actionCreators'
import { DocumentType } from '../document_types/model'
import * as documentTypesActionCreators from '../document_types/actionCreators'

// Fetch

const fetchVehicleDocumentsEpic = (action$, store) => {
    const tableIdentifier = 'vehicle_documents_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `vehicle-documents/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(vehicleDocumentSchema)
                        )

                        const vehicleDocuments = normalizedEntitiesToRecordMap(
                            normalizedData.entities.vehicle_documents,
                            VehicleDocument,
                            normalizedData.result
                        )
                        const vehicles = normalizedEntitiesToRecordMap(normalizedData.entities.vehicles, Vehicle)
                        const documentTypes = normalizedEntitiesToRecordMap(normalizedData.entities.document_types, DocumentType)

                        const observables = [
                            Observable.of(vehiclesActionCreators.fetchVehiclesFulfilled(vehicles)),
                            Observable.of(documentTypesActionCreators.fetchDocumentTypesFulfilled(documentTypes)),
                            Observable.of(actionCreators.fetchVehicleDocumentsFulfilled(vehicleDocuments)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchVehicleDocumentsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchVehicleDocumentEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().vehicleDocuments.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `vehicle-documents/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, vehicleDocumentSchema)

                        const vehicleDocuments = normalizedEntitiesToRecordMap(normalizedData.entities.vehicle_documents, VehicleDocument)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchVehicleDocumentsFulfilled(vehicleDocuments)),
                            Observable.of(actionCreators.fetchVehicleDocumentFulfilled())
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchVehicleDocumentsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveVehicleDocumentEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().vehicleDocuments.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = Object.assign({}, action.payload.values)
            if (action.payload.ignoreDuplicity) {
                values.ignore_duplicity = 1
            }

            let path = `vehicle-documents`
            let method = 'POST'

            if (values.id) {
                path = `vehicle-documents/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        if (ajaxResponse.response.duplicity_found) {
                            return Observable.concat(Observable.of(actionCreators.saveVehicleDocumentDuplicityFound(ajaxResponse.response.duplicity)))
                        }

                        const normalizedData = normalize(ajaxResponse.response, vehicleDocumentSchema)

                        const vehicleDocuments = normalizedEntitiesToRecordMap(normalizedData.entities.vehicle_documents, VehicleDocument)
                        const vehicleDocument = vehicleDocuments.valueSeq().first()

                        return Observable.of(actionCreators.saveVehicleDocumentFulfilled(vehicleDocument))
                    })
                    .catch(error => Observable.of(actionCreators.saveVehicleDocumentRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteVehicleDocumentEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().vehicleDocuments.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `vehicle-documents/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteVehicleDocumentFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteVehicleDocumentRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Fetch files

const fetchVehicleDocumentFilesEpic = (action$, store) =>
action$
    .ofType(actionTypes.FETCH_FILES)
    .filter(() => !store.getState().vehicleDocuments.getIn(['files', 'fetching']))
    .switchMap(action =>
        Observable.concat(
            Observable.of({
                type: actionTypes.FETCH_FILES_STARTED,
            }),
            request({
                path: `vehicle-documents/${action.payload}/files`,
                method: 'GET',
            })
                .switchMap(ajaxResponse => Observable.of(actionCreators.fetchVehicleDocumentFilesFulfilled(ajaxResponse.response.files)))
                .catch(error => Observable.of(actionCreators.fetchVehicleDocumentFilesRejected(parseAPIError(error))))
                .takeUntil(action$.ofType(actionTypes.FETCH_FILES_CANCELLED))
        )
    )

// Upload files

const uploadVehicleDocumentFilesEpic = (action$, store) =>
action$
    .ofType(actionTypes.UPLOAD_FILES)
    .filter(() => !store.getState().vehicleDocuments.getIn(['files', 'saving']))
    .switchMap(action =>
        Observable.concat(
            Observable.of({
                type: actionTypes.UPLOAD_FILES_STARTED,
            }),
            request({
                path: `vehicle-documents/${action.payload.id}/files`,
                method: 'POST',
                body: { ...action.payload },
            })
                .switchMap(ajaxResponse => Observable.of(actionCreators.uploadVehicleDocumentFilesFulfilled(ajaxResponse.response.files)))
                .catch(error => Observable.of(actionCreators.uploadVehicleDocumentFilesRejected(parseAPIError(error))))
                .takeUntil(action$.ofType(actionTypes.UPLOAD_FILES_CANCELLED))
        )
    )

// Delete file

const deleteVehicleDocumentFileEpic = (action$, store) =>
action$
    .ofType(actionTypes.DELETE_FILE)
    .filter(() => !store.getState().vehicleDocuments.getIn(['files', 'deleting']))
    .switchMap(action =>
        Observable.concat(
            Observable.of({
                type: actionTypes.DELETE_FILE_STARTED,
            }),
            request({
                path: `vehicle-documents/${action.payload.id}/files/${action.payload.file_id}`,
                method: 'DELETE',
            })
                .switchMap(ajaxResponse => Observable.of(actionCreators.deleteVehicleDocumentFileFulfilled(ajaxResponse.response.files)))
                .catch(error => Observable.of(actionCreators.deleteVehicleDocumentFileRejected(parseAPIError(error))))
                .takeUntil(action$.ofType(actionTypes.DELETE_FILE_CANCELLED))
        )
    )

// Export

const exportVehicleDocumentsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `vehicle-documents/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

export default [fetchVehicleDocumentsEpic, fetchVehicleDocumentEpic, saveVehicleDocumentEpic, deleteVehicleDocumentEpic, exportVehicleDocumentsEpic, fetchVehicleDocumentFilesEpic, uploadVehicleDocumentFilesEpic, deleteVehicleDocumentFileEpic]
